import type { MoneyDto } from '@compflash/contracts';

export function formatMoney(money: MoneyDto | null | undefined): string {
  if (!money) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: money.currency }).format(money.amountMinor / 100);
}

export function formatPercent(fraction: number | null | undefined, withSign = true): string {
  if (fraction === null || fraction === undefined) return '—';
  const sign = withSign && fraction > 0 ? '+' : '';
  return `${sign}${(fraction * 100).toFixed(1)}%`;
}

export function formatDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—';
  if (days < 1) return 'today';
  if (days < 2) return '1 day ago';
  if (days < 60) return `${Math.round(days)} days ago`;
  return `${Math.round(days / 30)} months ago`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

export function describeIdentity(identity: Record<string, unknown> | null | undefined): string {
  if (!identity) return 'Unidentified card';
  const serial = identity['serial'] as { printRun?: number | null } | null | undefined;
  const parts = [
    identity['seasonLabel'] ?? identity['year'],
    identity['manufacturer'],
    identity['setName'],
    identity['subset'],
    identity['subject'],
    identity['cardNumber'] ? `#${String(identity['cardNumber'])}` : null,
    identity['parallel'],
    serial?.printRun ? `/${serial.printRun}` : null,
    identity['isAutograph'] ? 'Auto' : null,
    identity['isRelic'] ? 'Patch' : null,
    identity['isRookie'] ? 'RC' : null,
  ].filter((part) => part !== null && part !== undefined && part !== '');
  return parts.length ? parts.join(' ') : 'Unidentified card';
}

export function describeCondition(
  condition: { kind: string; grade?: { company: string; grade: number | null } | null; estimate?: string } | null | undefined,
): string {
  if (!condition) return 'Unknown condition';
  if (condition.kind === 'graded' && condition.grade) {
    return `${condition.grade.company} ${condition.grade.grade ?? 'AUTH'}`;
  }
  if (condition.kind === 'raw') {
    return `Raw${condition.estimate && condition.estimate !== 'unknown' ? ` (${condition.estimate.replace(/_/g, ' ')})` : ''}`;
  }
  return 'Unknown condition';
}

/** Convert a File or Blob into the base64 payload the scan endpoint accepts. */
export async function fileToBase64(file: Blob): Promise<{ base64: string; mimeType: string }> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return { base64: btoa(binary), mimeType: file.type || 'image/jpeg' };
}
